import React, { useState, useEffect, useRef, Suspense } from "react";
import { useTranslation } from "react-i18next";

import "./styles/Layout.css";

import { useIsPortrait } from "./hooks/useIsPortrait";

import { getScoreData, type ScoreData } from "./services/api";

import CareerMap from "./components/CareerMap/CareerMap";
import Avatar from "./components/Avatar/Avatar";
import ConsultationButtons from "./components/ConsultationButtons";
import ConsultationDialog from "./components/ConsultationDialog";
import { useFetchBriefing } from "./hooks/useFetchBriefing";

import logoImg from "./assets/logo.png";

import { ConsultationProvider } from "./context/ConsultationProvider";
import { Phase } from "./types";
import { useWakeLock } from "./hooks/useWakeLock";
import { useConsultation } from "./context/useConsultation";
import type { ModalConfig } from "./context/ConsultationContext";
import { captureAppException } from "./sentry";

const ConsultationScreen: React.FC = () => {
  const {
    scoreData,
    setScoreData,
    phase,
    setPhase,
    flowMode,
    setFlowMode,
    setBriefingDone,
    mouthRef,
    isThinking,
    modalConfig,
    setModalConfig,
  } = useConsultation();

  // 특정 페이즈에서 화면 켜짐 유지
  useWakeLock(
    phase === Phase.READING_SCRIPT || phase === Phase.LIVE_CONSULTATION,
  );

  const [isLoading, setIsLoading] = useState(false);

  const isPortrait = useIsPortrait();

  const { t } = useTranslation();
  const { fetchBriefing } = useFetchBriefing();

  useEffect(() => {
    // 초기 모달 설정
    setModalConfig({
      isOpen: true,
      title: t("audioWarningTitle"),
      message: t("audioWarningMessage"),
      showProgress: false,
    });
  }, [setModalConfig, t]);

  useEffect(() => {
    const initApp = async () => {
      setIsLoading(true);
      const params = new URLSearchParams(window.location.search);
      const returnUrl = params.get("return_url");
      const requestedMode = params.get("mode");

      if (returnUrl) sessionStorage.setItem("return_url", returnUrl);
      if (requestedMode)
        sessionStorage.setItem("requested_mode", requestedMode);

      if (returnUrl || requestedMode) {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.delete("return_url");
        nextUrl.searchParams.delete("mode");
        const cleanedPath = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
        window.history.replaceState({}, "", cleanedPath);
      }

      try {
        const data = await getScoreData(requestedMode);
        setScoreData(data);
        if (data.flowMode) {
          setFlowMode(data.flowMode);
        }
        // 브리핑을 끝까지 들은 적이 있으면 재생 화면에서 건너뛰기를 허용하고,
        // 실시간 상담 전용(liveOnly)이 아니라면 브리핑 완료 화면으로 바로 진입
        if (data.briefingDone) {
          setBriefingDone(true);
          if (data.flowMode !== "liveOnly") {
            setPhase(Phase.BRIEFING_COMPLETED);
          }
        }
      } catch (err: unknown) {
        captureAppException(err, {
          tags: { feature: "app_bootstrap" },
          extra: { requestedMode },
        });
        const errMsg = err instanceof Error ? err.message : t("dataLoadFailed");
        setModalConfig({
          isOpen: true,
          title: t("errorOccurred"),
          message: errMsg,
          showProgress: false,
          type: "alert",
        });
      } finally {
        setIsLoading(false);
      }
    };
    initApp();
  }, [setFlowMode, setModalConfig, setScoreData, setBriefingDone, setPhase, t]);

  useEffect(() => {
    if (!scoreData || flowMode === "liveOnly") return;
    fetchBriefing();
  }, [scoreData, flowMode, fetchBriefing]);

  const transformScoreToArray = (data: ScoreData): number[] => {
    if (!data) return [];
    const { score } = data;
    return [
      Number(score.lbi),
      Number(score.rbi),
      Number(score.pei),
      Number(score.ai),
      Number(score.hi),
      Number(score.wci),
    ];
  };

  return (
    <Suspense
      fallback={
        <div className="loading-wrapper">
          <div className="loading-container">
            <div className="loading-spinner">
              <span className="spinner-object"></span>
            </div>
          </div>
        </div>
      }
    >
      {isPortrait ? (
        <div className="portrait-overlay">
          <div>
            <div className="portrait-msg-title">{t("portraitMsgTitle")}</div>
            <div className="portrait-msg-subtitle">
              {t("portraitMsgSubtitle")}
            </div>
          </div>
        </div>
      ) : (
        <ConsultationDialog
          title={modalConfig.title}
          message={modalConfig.message}
          isOpen={modalConfig.isOpen}
          showProgress={modalConfig.showProgress}
          type={modalConfig.type}
          onConfirm={modalConfig.onConfirm}
          onClose={() =>
            setModalConfig((prev: ModalConfig) => ({ ...prev, isOpen: false }))
          }
        />
      )}

      <div className="ai-wrapper">
        {scoreData && (
          <div className="map-wrapper">
            <CareerMap score={transformScoreToArray(scoreData)} />
          </div>
        )}

        {isLoading && (
          <div className="loading-wrapper">
            <div className="loading-container">
              <div className="loading-spinner">
                <span className="spinner-object"></span>
              </div>
            </div>
          </div>
        )}

        <div
          className="logo-wrapper"
          style={{ backgroundImage: `url(${logoImg})` }}
        />

        <Avatar mouthRef={mouthRef} isThinking={isThinking} />

        <ConsultationButtons isPortrait={isPortrait} />
      </div>
    </Suspense>
  );
};

const App: React.FC = () => {
  const mouthRef = useRef<HTMLDivElement>(null);

  return (
    <ConsultationProvider mouthRef={mouthRef}>
      <ConsultationScreen />
    </ConsultationProvider>
  );
};

export default App;
